import React, { useEffect, useRef, useState } from 'react';
import { Input, message, Modal, Spin } from 'antd';
import { getTextContent, saveTextContent } from '../../helpers/file';
import { useTranslation } from '../../hooks/useTranslation';

interface TextEditModalProps {
  open: boolean;
  uploadId: string | number;
  fileName: string;
  onCancel: () => void;
  onSaved?: () => void;
}

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
};

const TextEditModal: React.FC<TextEditModalProps> = ({
  open,
  uploadId,
  fileName,
  onCancel,
  onSaved,
}) => {
  const { t } = useTranslation();
  const tRef = useRef(t);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  useEffect(() => {
    if (!open) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setContent('');

    getTextContent(uploadId)
      .then((text) => {
        if (!cancelled) {
          setContent(text);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          message.error(getErrorMessage(error, tRef.current('textEdit.loadFailed')));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, uploadId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await saveTextContent(uploadId, content);
      message.success(result?.message || t('textEdit.saveSuccess'));
      onCancel();
      onSaved?.();
    } catch (error) {
      message.error(getErrorMessage(error, t('textEdit.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={t('textEdit.title', { fileName })}
      open={open}
      onCancel={saving ? undefined : onCancel}
      onOk={handleSave}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      confirmLoading={saving}
      okButtonProps={{ disabled: loading }}
      width={860}
      destroyOnClose
    >
      <div data-text-edit-modal="true">
        <Spin spinning={loading}>
          <Input.TextArea
            value={content}
            onChange={(event) => setContent(event.target.value)}
            onPaste={(event) => event.stopPropagation()}
            rows={18}
            placeholder={t('textEdit.placeholder')}
            style={{ fontFamily: 'Menlo, Consolas, monospace' }}
          />
        </Spin>
      </div>
    </Modal>
  );
};

export default TextEditModal;
